// apps/web/src/pages/author/sections/WidgetSlots.tsx
//
// Phase 8 Plan 08 Task 3 — WidgetSlots form section (authr-01 / authr-03).
//
// recipe.widgetSlots: SlotDeclaration[]   each: { slug: string; zone: 'options' | 'main' }
//
// SCHEMA NOTE: the SlotDeclaration schema (packages/shared/src/schemas/
// recipe-v1.ts lines 28-40) uses { slug, zone } — NOT { slotId, label } as
// the plan's prose described. Schema is the single source of truth; we
// render the actual schema fields here.
// slug: kebab-case, max 50; zone: 'options' | 'main' (D-06 'controls' is
// engine-only and must NEVER appear here).
//
// authr-03 + authr-06 hygiene preserved.

import type { RecipeDefinitionV1 } from "@the-recipe-spread/shared";
import { useFieldArray, useFormContext } from "react-hook-form";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FieldArrayRow } from "../FieldArrayRow";

const NEW_SLOT_DEFAULTS = { slug: "", zone: "main" as const };

const ZONE_OPTIONS = ["options", "main"] as const;

export function WidgetSlots(): React.ReactElement {
  const { control } = useFormContext<RecipeDefinitionV1>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "widgetSlots",
  });

  return (
    <Fieldset legend="WIDGET SLOTS">
      {fields.length === 0 ? (
        <EmptyState
          title="No widget slots yet."
          cta={{
            label: "Add widget slot",
            onClick: () => append(NEW_SLOT_DEFAULTS),
          }}
        />
      ) : (
        <>
          {fields.map((field, index) => (
            <FieldArrayRow
              key={field.id}
              singular="widget slot"
              index={index}
              canMoveUp={index > 0}
              canMoveDown={index < fields.length - 1}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
              onRemove={() => remove(index)}
              deleteDialogTitle="Delete widget slot?"
              deleteDialogBody="This widget slot will be removed from the recipe."
              deleteConfirmLabel="Delete widget slot"
            >
              <FormField
                control={control}
                name={`widgetSlots.${index}.slug`}
                render={({ field }) => (
                  <FormItem data-field-path={`widgetSlots.${index}.slug`}>
                    <FormLabel>Slug (kebab-case)</FormLabel>
                    <FormControl>
                      <Input {...field} autoFocus={!field.value} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`widgetSlots.${index}.zone`}
                render={({ field }) => (
                  <FormItem data-field-path={`widgetSlots.${index}.zone`}>
                    <FormLabel>Zone</FormLabel>
                    <Select value={field.value ?? "main"} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="main" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ZONE_OPTIONS.map((z) => (
                          <SelectItem key={z} value={z}>
                            {z}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FieldArrayRow>
          ))}
          <button
            type="button"
            onClick={() => append(NEW_SLOT_DEFAULTS)}
            className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Add widget slot
          </button>
        </>
      )}
    </Fieldset>
  );
}
WidgetSlots.displayName = "WidgetSlots";
